/**
 * Evaluate whether an act's conditions are met for a given context.
 *
 * NOTE: This function is duplicated in frontend and backend.
 * Backend counterpart: app/server/apiGateway/src/shared/utils/act-conditions.util.ts
 * Keep both copies in sync. Unit tests in both copies guard against divergence.
 */

export type ActConditionType = 'always' | 'time' | 'patient_age' | 'staff'

export type ActCondition = {
  type: ActConditionType
  after?: string   // HH:mm 24h
  before?: string  // HH:mm 24h
  minAge?: number
  maxAge?: number
  staffIds?: string[] // staff: Staff._id list the act is restricted to
}

export type ActConditionContext = {
  consultationEndDate: Date
  patientDob?: Date
  /** Staff._id of the practitioner matched by the config's role (doctor for MEDECIN, nurse for PDS). */
  practitionerId?: string
}

export function evaluateActConditions(conditions: ActCondition[], context: ActConditionContext): boolean {
  if (!conditions || conditions.length === 0) return true
  return conditions.every((c) => evaluateSingleCondition(c, context))
}

/** True when the config is restricted to a list of practitioners (has a non-empty `staff` condition). */
export function isPractitionerSpecific(conditions: ActCondition[] | undefined): boolean {
  return (conditions ?? []).some((c) => c.type === 'staff' && Array.isArray(c.staffIds) && c.staffIds.length > 0)
}

/**
 * Practitioner-specific configs REPLACE the generic ones.
 *
 * Given configs whose conditions have already been evaluated as met, drop every generic
 * (non practitioner-specific) config of an establishment + role group that also contains a
 * firing practitioner-specific config.
 */
export function applyPractitionerOverride<T extends { establishmentId?: string; role?: string; conditions?: ActCondition[] }>(
  firingConfigs: T[]
): T[] {
  const overriddenGroups = new Set(
    firingConfigs.filter((c) => isPractitionerSpecific(c.conditions)).map((c) => `${c.establishmentId ?? ''}:${c.role ?? 'MEDECIN'}`)
  )
  if (overriddenGroups.size === 0) return firingConfigs
  return firingConfigs.filter(
    (c) => isPractitionerSpecific(c.conditions) || !overriddenGroups.has(`${c.establishmentId ?? ''}:${c.role ?? 'MEDECIN'}`)
  )
}

function evaluateSingleCondition(c: ActCondition, ctx: ActConditionContext): boolean {
  if (c.type === 'always') return true

  if (c.type === 'staff') {
    if (!ctx.practitionerId) return false
    return (c.staffIds ?? []).includes(ctx.practitionerId)
  }

  if (c.type === 'time') {
    // Use Paris timezone — the server runs UTC but billing conditions are always French local time
    const hhmm = (d: Date) => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Paris',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(d)
      const h = parseInt(parts.find((p) => p.type === 'hour')!.value, 10)
      const m = parseInt(parts.find((p) => p.type === 'minute')!.value, 10)
      return h * 60 + m
    }
    const t = hhmm(ctx.consultationEndDate)
    const after = c.after
      ? (() => {
          const [h, m] = c.after.split(':').map(Number)
          return h * 60 + m
        })()
      : 0
    const rawBefore = c.before
      ? (() => {
          const [h, m] = c.before.split(':').map(Number)
          return h * 60 + m
        })()
      : 24 * 60
    // Treat before:"00:00" (midnight = 0 minutes) as end-of-day (24:00 = 1440 minutes)
    const before = rawBefore === 0 ? 24 * 60 : rawBefore
    if (after <= before) return t >= after && t < before
    return t >= after || t < before
  }

  if (c.type === 'patient_age') {
    if (!ctx.patientDob) return false
    const ageYears = Math.floor(
      (ctx.consultationEndDate.getTime() - ctx.patientDob.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    )
    if (c.minAge !== undefined && ageYears < c.minAge) return false
    if (c.maxAge !== undefined && ageYears > c.maxAge) return false
    return true
  }

  return false
}
